/**
 * "Üretim" sekmesi — Power BI raporundaki ikinci sayfanın karşılığı.
 *
 * Solda temizle düğmesi, iki kart ve dört slicer; ortada sipariş
 * tablosu ("Sipariş, Gövde Stok Takibi"), sağda mamul bazında üretim/stok
 * tablosu. Satırlar `api.js`'teki `uretimSatirlari` ile iki ucun birleşimi.
 *
 * Rapordan BİLEREK ayrılan iki nokta (ikisi de modeldeki tek yönlü
 * ProdResults → StartStop ilişkisinden):
 *  - Sipariş tablosu raporda her ürün satırını ayrı basıyor ama StartStop
 *    sütunlarını (Bitiş, Sipariş, Üretim, HDS) satıra göre süzemediği için
 *    her satırda tablonun en küçük değerini gösteriyor. Burada tablo periyot
 *    başına tek satır ve StartStop sütunları o periyodun değeri.
 *  - Mamul tablosundaki ID / Order No raporda tarihteki TÜM StartStop
 *    satırlarının toplamı (her satırda aynı sayı). Burada periyodun kendi
 *    StartStop satırının ID'si ve sipariş numarası.
 */
import { PROD_PATH, STARTSTOP_PATH } from "../api";
import {
  BREAKPOINTS,
  metinKarti,
  slicer,
  tabloKarti,
  temizle,
  tvSecici,
  yiginYerlesim,
} from "./ortak";

export const URETIM_ROWS = 24;

const LAYOUTS = yiginYerlesim([
  ["temizle", 2],
  ["kart-tarih", 2],
  ["kart-single", 3],
  ["f-renk", 3],
  ["f-us", 3],
  ["f-govde", 3],
  ["f-malzeme", 6],
  ["siparis-tablosu", 12, 12],
  ["mamul-tablosu", 12, 12],
]);

export function buildUretimConfig(rowPx, gap, refreshKey = 0, tvMode = false) {
  const V = tvSecici(tvMode);
  const q = `?_t=${refreshKey}`;
  const URETIM = { url: `${PROD_PATH}${q}#uretim` };
  const URETIM_GOVDE = { url: `${PROD_PATH}${q}#uretim-govde` };
  const KARTLAR = { url: `${STARTSTOP_PATH}${q}#uretim` };

  return {
    layout: { columns: 24, rowHeight: rowPx, gap, breakpoints: BREAKPOINTS },

    filters: [
      // Sol sütundaki logo kaldırıldı; sütun 2 satır yukarı kaydı, boşalan
      // 2 satırı Malzeme listesi aldı.
      temizle({ x: 0, y: 0, w: 3, h: 2 }, LAYOUTS),
      slicer("f-renk", "color", "Renk", { x: 0, y: 4, w: 3, h: 3 }, LAYOUTS),
      slicer("f-us", "u_single", "U / Single", { x: 0, y: 7, w: 3, h: 3 }, LAYOUTS),
      slicer(
        "f-govde",
        "govde_and_govde_on",
        "Gövde / Gövde Ön",
        { x: 0, y: 10, w: 3, h: 3 },
        LAYOUTS
      ),
      slicer(
        "f-malzeme",
        "product",
        "Malzeme",
        { x: 0, y: 13, w: 3, h: 8 },
        LAYOUTS,
        { selectAll: true }
      ),
    ],

    tiles: [
      // Raporda başlığı gizli, değeri planın ilk günü.
      metinKarti("kart-tarih", null, KARTLAR, "ilk_tarih", { x: 0, y: 2, w: 3, h: 2 }, LAYOUTS, V),
      metinKarti(
        "kart-single",
        "Single Görmeme",
        KARTLAR,
        "single_gormeme",
        // Başlık şeridi + değer TV modunda 2 satıra (≈55 px) sığmıyordu.
        { x: 0, y: 21, w: 3, h: 3 },
        LAYOUTS,
        V
      ),

      tabloKarti(
        "siparis-tablosu",
        "Sipariş, Gövde Stok Takibi",
        URETIM_GOVDE,
        [
          { key: "order_nr", label: "Order Number", align: "center" },
          { key: "tarih", label: "Tarih" },
          { key: "baslangic", label: "Başlangıç" },
          { key: "bitis", label: "Bitiş" },
          { key: "siparis", label: "Sipariş", wrap: true },
          { key: "ss_uretim", label: "Üretim", align: "right", format: "int" },
          // Tek toplanan sütun: periyottaki gövde ürünlerinin tüketimi.
          { key: "demand", label: "Toplam Tüketim", agg: "sum", align: "right", format: "int" },
          { key: "hds", label: "Toplam HDS Stok", align: "right", format: "int" },
        ],
        { x: 3, y: 0, w: 11, h: 24 },
        LAYOUTS,
        V,
        { groupBy: ["periyot"] }
      ),

      tabloKarti(
        "mamul-tablosu",
        "Mamul Bazında Üretim ve Stok",
        URETIM,
        [
          { key: "ss_id", label: "ID" },
          { key: "ss_order_nr", label: "Order No", align: "center" },
          { key: "ss_tarih", label: "Tarih" },
          { key: "saat", label: "Saat" },
          // Raporda tanıma göre gruplanıyor; aynı tanımı paylaşan
          // malzemelerin kodları yan yana yazılır (raporda kodların toplamı
          // basılıyordu).
          { key: "product", label: "Malzeme", agg: "join", wrap: true },
          { key: "tanim", label: "Tanım" },
          { key: "production", label: "Üretim", agg: "sum", align: "right", format: "int" },
          { key: "demand", label: "Tüketim", agg: "sum", align: "right", format: "int" },
          { key: "emniyet", label: "Emniyet Stok", agg: "sum", align: "right", format: "int" },
          { key: "inventory", label: "Stok", agg: "sum", align: "right", format: "int" },
        ],
        { x: 14, y: 0, w: 10, h: 24 },
        LAYOUTS,
        V,
        { groupBy: ["periyot", "tanim"] }
      ),
    ],
  };
}
